import type { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'

import { $taskPolling } from '@/api/helpers'
import {
  refreshWaitingRoomAutoSyncedTargets,
  type WaitingRoomAutoSyncResult,
} from '@/helpers/events/waitingRoomAutoSync'
import { useSnackbarStore } from '@/store/snackbar'

const isCompletedResult = (result: unknown): result is WaitingRoomAutoSyncResult => {
  if (!result || typeof result !== 'object') return false

  const value = result as Partial<WaitingRoomAutoSyncResult>

  return value.status === 'completed' && Array.isArray(value.affected_targets)
}

export const useWaitingRoomAutoSyncStore = defineStore({
  id: 'waitingRoomAutoSync',
  persist: false,
  state: () => ({
    activeTaskIds: [] as string[],
  }),

  actions: {
    handleResponse<T extends AxiosResponse>(response: T): T {
      const syncPayload = response.data
      const taskId = syncPayload?.waiting_room_sync_task_id

      if (taskId && Object.prototype.hasOwnProperty.call(syncPayload, 'result')) {
        window.setTimeout(() => this.startTask(taskId), 0)
        response.data = syncPayload.result
      }

      return response
    },

    startTask(taskId: string) {
      if (!taskId || this.activeTaskIds.includes(taskId)) return

      this.activeTaskIds.push(taskId)
      void this.pollTask(taskId)
    },

    refreshTargets(result: WaitingRoomAutoSyncResult) {
      refreshWaitingRoomAutoSyncedTargets(result)
    },

    async pollTask(taskId: string) {
      const startedAt = Date.now()

      try {
        while (Date.now() - startedAt < 305000) {
          const response = await $taskPolling(taskId)
          const status = response.data?.status

          if (status === 'SUCCESS') {
            const result = response.data?.result
            if (
              isCompletedResult(result)
              && ((result.accepted_count || 0) > 0 || (result.advanced_count || 0) > 0)
            ) {
              this.showCompletedMessage(result)
            }
            return
          }

          if (status === 'FAILURE' || status === 'REVOKED') {
            return
          }

          await new Promise(resolve => window.setTimeout(resolve, 1000))
        }
      } catch {
        // Polling failures are intentionally silent.
      } finally {
        this.activeTaskIds = this.activeTaskIds.filter(id => id !== taskId)
      }
    },

    showCompletedMessage(result: WaitingRoomAutoSyncResult) {
      const snackbarStore = useSnackbarStore()
      const syncedFiles = result.accepted_count
      const advancedItems = result.advanced_count || 0
      const linkedItems = result.managed_link_count ?? result.affected_targets.length
      const title = syncedFiles && advancedItems
        ? 'Files and statuses synced'
        : syncedFiles
          ? 'Files synced from Waiting Room'
          : 'Item statuses autoapproved'
      const text = syncedFiles && advancedItems
        ? `${syncedFiles} file(s) added to Item Files (${linkedItems} item link(s)); ${advancedItems} item status(es) autoapproved from existing files.`
        : syncedFiles
          ? `${syncedFiles} file(s) added to Item Files (${linkedItems} item link(s)).`
          : `${advancedItems} item status(es) autoapproved from existing File Management files.`

      snackbarStore.showMessage({
        color: 'success',
        icon: 'mdi-checkbox-marked-circle',
        title,
        text,
        timeout: -1,
        location: 'top end',
        actionLabel: 'Refresh',
        onAction: () => this.refreshTargets(result),
      })
    },
  },
})

export type WaitingRoomAutoSyncStore = ReturnType<typeof useWaitingRoomAutoSyncStore>
